import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import videoClipService from "../../services/videoClipService";

// Initial state
const initialState = {
  clips: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20,
    hasMore: false,
  },
  filters: {
    videoId: null,
    playerId: null,
  },
  loading: false,
  saveLoading: false,
  deleteLoading: {},
  error: null,
};

// Async thunks
export const saveVideoClip = createAsyncThunk(
  "videoClip/saveVideoClip",
  async (clipData, { rejectWithValue }) => {
    try {
      const response = await videoClipService.saveVideoClip(clipData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to save video clip"
      );
    }
  }
);

export const fetchVideoClips = createAsyncThunk(
  "videoClip/fetchVideoClips",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await videoClipService.getVideoClips(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch video clips"
      );
    }
  }
);

export const deleteVideoClip = createAsyncThunk(
  "videoClip/deleteVideoClip",
  async (clipId, { rejectWithValue }) => {
    try {
      await videoClipService.deleteVideoClip(clipId);
      return clipId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete video clip"
      );
    }
  }
);

// Slice
const videoClipSlice = createSlice({
  name: "videoClip",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        videoId: null,
        playerId: null,
      };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearClips: (state) => {
      state.clips = [];
      state.pagination = initialState.pagination;
    },
  },
  extraReducers: (builder) => {
    // Save video clip
    builder
      .addCase(saveVideoClip.pending, (state) => {
        state.saveLoading = true;
        state.error = null;
      })
      .addCase(saveVideoClip.fulfilled, (state, action) => {
        state.saveLoading = false;
        // Backend returns { success: true, message: "...", data: {...} }
        // Extract the actual clip data from the response
        const newClip = action.payload.data || action.payload;
        // Add new clip to the beginning of the array
        state.clips.unshift(newClip);
        state.pagination.totalCount += 1;
      })
      .addCase(saveVideoClip.rejected, (state, action) => {
        state.saveLoading = false;
        state.error = action.payload;
      });

    // Fetch video clips
    builder
      .addCase(fetchVideoClips.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVideoClips.fulfilled, (state, action) => {
        state.loading = false;
        // Backend returns data in 'data' field
        state.clips = action.payload.data || action.payload.items || action.payload.clips || [];

        // Backend returns pagination object
        const paginationData = action.payload.pagination || {};
        state.pagination = {
          currentPage: paginationData.currentPage || action.payload.currentPage || 1,
          totalPages: paginationData.totalPages || action.payload.totalPages || 1,
          totalCount: paginationData.totalClips || paginationData.totalItems || action.payload.totalCount || 0,
          limit: paginationData.limit || action.payload.limit || 20,
          hasMore: paginationData.hasMore || (paginationData.currentPage || 1) < (paginationData.totalPages || 1),
        };
      })
      .addCase(fetchVideoClips.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete video clip
    builder
      .addCase(deleteVideoClip.pending, (state, action) => {
        state.deleteLoading[action.meta.arg] = true;
        state.error = null;
      })
      .addCase(deleteVideoClip.fulfilled, (state, action) => {
        state.deleteLoading[action.payload] = false;
        delete state.deleteLoading[action.payload];
        // Remove clip from array - backend uses _id field
        state.clips = state.clips.filter((clip) => {
          const clipId = clip.clipId || clip._id;
          return clipId !== action.payload;
        });
        state.pagination.totalCount = Math.max(0, state.pagination.totalCount - 1);
      })
      .addCase(deleteVideoClip.rejected, (state, action) => {
        state.deleteLoading[action.meta.arg] = false;
        delete state.deleteLoading[action.meta.arg];
        state.error = action.payload;
      });
  },
});

export const { setFilters, clearFilters, clearError, clearClips } = videoClipSlice.actions;

export default videoClipSlice.reducer;
